import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Tone } from '@/lib/tones';
import { IconTile } from './icon-tile';
import logo from '@/assets/auto_logo.png';

interface StatusScreenProps {
    icon: LucideIcon;
    tone?: Tone;
    code?: string;
    title: string;
    description: React.ReactNode;
    actions?: React.ReactNode;
    /** Extra detail block, e.g. error diagnostics */
    children?: React.ReactNode;
    /** `page` = full-screen with logo (outside the app shell); `inline` = inside a layout */
    variant?: 'page' | 'inline';
}

/** Full-page outcome screens: 404, 403, crashes, "coming soon". */
export const StatusScreen: React.FC<StatusScreenProps> = ({
    icon, tone = 'neutral', code, title, description, actions, children, variant = 'page',
}) => (
    <div className={cn('flex flex-col items-center justify-center px-4 text-center', variant === 'page' ? 'min-h-screen bg-background py-12' : 'min-h-[60vh] py-10')}>
        {variant === 'page' && <img src={logo} alt="FraudWall Auto" className="mb-12 h-8 w-auto" />}
        <IconTile icon={icon} tone={tone} size="lg" />
        {code && <p className="overline-label mt-6">{code}</p>}
        <h1 className={cn('font-display text-title text-foreground md:text-display', code ? 'mt-2' : 'mt-6')}>{title}</h1>
        <p className="mt-3 max-w-md text-body text-muted-foreground">{description}</p>
        {children && <div className="mt-6 w-full max-w-md">{children}</div>}
        {actions && <div className="mt-8 flex flex-wrap items-center justify-center gap-3">{actions}</div>}
    </div>
);
